import { useMemo, useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import axios from "axios"
import moment from "moment"
import nProgress from "nprogress"

import Label from "components/forms/label"
import OutletFilterSelect from "components/forms/outlet-filter-select"
import InputDate from "components/forms/input-date"
import { getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"
import useBranchRepositories from "repositories/branch"
import useOutstandingPoReportRepositories from "repositories/outstanding-po-report"
import { useAuth } from "contexts/auth-context"

const formatNumber = (value) => new Intl.NumberFormat("id-ID").format(Number(value ?? 0))
const formatDateID = (value) => (value ? moment(value).format("DD/MM/YYYY") : "-")
// ATALLA-356: toggle tampilan qty PCS vs LUSIN (1 lusin = 12 pcs). Ini murni display —
// nilai dasar dari backend selalu PCS; mode LUSIN membaginya 12.
const LUSIN_FACTOR = 12
const formatQtyByUnit = (value, unit) => {
    const num = Number(value ?? 0)
    if (unit === "LUSIN") {
        return new Intl.NumberFormat("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num / LUSIN_FACTOR)
    }
    return new Intl.NumberFormat("id-ID").format(num)
}

const STATUS_OPTIONS = [
    { id: "", name: "Semua Status" },
    { id: "DRAFT", name: "Draft" },
    { id: "APPROVED", name: "Approved" },
    { id: "PARTIAL_RECEIVED", name: "Partial Received" },
]

export default function List() {
    const { isSuper, currentBranch, canFilterAllOutlets } = useAuth()
    const [searchParams, setSearchParams] = useSearchParams()
    const [importing, setImporting] = useState(false)
    // Outlet tujuan upload sisa PO. WAJIB dipilih dulu sebelum pilih file — file initial
    // (Atalla / Vision) tidak memuat outlet, jadi operator menentukannya di sini.
    const [importOutletId, setImportOutletId] = useState("")
    // ATALLA-353: bila dicentang, qty pada file initial dianggap dalam LUSIN dan
    // dikalikan 12 oleh backend saat import (ordered/arrived/outstanding).
    const [convertLusinToPcs, setConvertLusinToPcs] = useState(false)

    const isBranchLocked = !canFilterAllOutlets() && !!currentBranch?.id // ATALLA-403: pemegang permission filter-entity bebas pilih outlet entitasnya
    const rawOutletId = searchParams.get("outlet_id") || getDefaultOutletFilterValue()
    const outletId = isBranchLocked ? String(currentBranch.id) : rawOutletId
    const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(outletId)
    // Untuk user terkunci cabang, outlet upload = cabangnya (otomatis). Untuk super, ambil
    // dari dropdown khusus upload di bawah.
    const effectiveImportOutletId = isBranchLocked ? String(currentBranch.id) : importOutletId
    const from = searchParams.get("from")
    const to = searchParams.get("to")
    const status = searchParams.get("status") || ""
    // ATALLA-356: satuan tampilan qty (PCS default / LUSIN). Disimpan di URL agar ikut
    // terkirim ke endpoint export.
    const displayUnit = (searchParams.get("unit") || "PCS").toUpperCase() === "LUSIN" ? "LUSIN" : "PCS"

    // 290726 (permintaan user): paginasi laporan. Dihitung PER PO di backend —
    // tabel dikelompokkan per PO, jadi satu PO tidak pernah terpotong antar halaman.
    const page = Number(searchParams.get("page") || 1) || 1

    const { data: reportData, isLoading, mutate } = useOutstandingPoReportRepositories({
        outlet_id: resolvedOutletCsv || undefined,
        date_from: from || undefined,
        date_to: to || undefined,
        status: status || undefined,
        page,
    })

    const { data: branchData, isLoading: isLoadingBranchSelection } = useBranchRepositories({
        paginate: false,
        excludeYoiboi: true,
    })

    // Opsi outlet khusus upload: outlet konkret saja (tanpa "Semua Outlet"), karena
    // data initial harus dikaitkan ke satu outlet.
    const importBranchOptions = useMemo(() => {
        return Array.isArray(branchData) ? branchData : (branchData?.data ?? [])
    }, [branchData])

    useEffect(() => {
        if (isLoading) {
            nProgress.start()
        } else {
            nProgress.done()
        }
    }, [isLoading])

    const mergeParams = (next) => {
        const current = Object.fromEntries(searchParams.entries())
        // 290726: mengubah FILTER selalu kembali ke halaman 1 — kalau tidak, user yang
        // sedang di halaman 12 lalu mempersempit filter akan mendarat di halaman yang
        // isinya jauh berbeda. Navigasi halaman sendiri (next.page ada) dikecualikan.
        const resetPage = next.page === undefined ? { page: null } : {}
        const merged = { ...current, ...resetPage, ...next }
        Object.keys(merged).forEach((key) => {
            if (merged[key] === null || merged[key] === undefined || merged[key] === "") {
                delete merged[key]
            }
        })
        setSearchParams(merged)
    }

    const handleImportFile = async (e) => {
        const file = e.target.files?.[0]
        e.target.value = ""
        if (!file) return

        // Guard: outlet wajib dipilih dulu (walau input di-disable, jaga-jaga).
        if (!effectiveImportOutletId) {
            alert("Pilih outlet tujuan upload terlebih dahulu sebelum memilih file.")
            return
        }

        setImporting(true)
        try {
            const form = new FormData()
            form.append("file", file)
            form.append("outlet_id", effectiveImportOutletId)
            // ATALLA-353: konversi qty file (LUSIN) -> PCS di backend saat dicentang.
            if (convertLusinToPcs) form.append("convert_lusin_to_pcs", "1")
            const res = await axios.post(
                `${process.env.REACT_APP_INVENTORY_API_URL}/reports/outstanding-po/import`,
                form,
                { headers: { "Content-Type": "multipart/form-data" } }
            )
            alert(res?.data?.message || "Import selesai.")
            mutate?.()
        } catch (err) {
            alert(err?.response?.data?.message || "Gagal import file.")
        } finally {
            setImporting(false)
        }
    }

    const rows = reportData?.rows ?? []
    const summary = reportData?.summary ?? {}

    // ATALLA-354: export xlsx dari view yang sedang difilter. Param mengikuti list +
    // unit (ATALLA-356) agar backend bisa memformat kolom qty sesuai satuan tampilan.
    const handleExport = () => {
        const query = new URLSearchParams()
        query.set("outlet_id", resolvedOutletCsv)
        if (from) query.set("date_from", from)
        if (to) query.set("date_to", to)
        if (status) query.set("status", status)
        query.set("unit", displayUnit)
        window.open(
            `${process.env.REACT_APP_INVENTORY_API_URL}/reports/outstanding-po/export?${query.toString()}`,
            "_blank"
        )
    }

    const branchNameById = useMemo(() => {
        const list = Array.isArray(branchData) ? branchData : (branchData?.data ?? [])
        const map = new Map()
        for (const b of list) map.set(String(b.id), b.name)
        return map
    }, [branchData])

    // Group rows per PO for readable rendering
    const groupedByPo = useMemo(() => {
        const map = new Map()
        for (const row of rows) {
            const key = String(row.po_id)
            if (!map.has(key)) map.set(key, [])
            map.get(key).push(row)
        }
        return Array.from(map.values())
    }, [rows])

    return (
        <div className="space-y-6">
            <div className="p-5 bg-white border rounded-2xl border-neutral-200 space-y-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <h2 className="text-lg font-semibold text-neutral-900">Sisa PO Belum Diterima</h2>
                        <p className="mt-1 text-sm text-neutral-500">
                            Item purchase order dengan qty diterima kurang dari qty dipesan (PO aktif) + initial data PO pabrik (import Excel).
                        </p>
                    </div>
                    {/* ATALLA-284: upload initial data PO luar-sistem (format ORDERNO/TGLORDER/KODE/
                        JUMORDER/JUMDATANG/SISA ORDER/TGESTIMASI/SELESAI, satu sheet per gudang).
                        Upload WAJIB pilih outlet tujuan dulu — file (Atalla/Vision) tidak memuat
                        outlet, jadi operator memilihnya sebelum memilih file. */}
                    <div className="flex flex-col items-stretch gap-2 w-full sm:w-72">
                        {!isBranchLocked && (
                            <div>
                                <Label value="Outlet Tujuan Upload" />
                                <select
                                    value={importOutletId}
                                    onChange={(e) => setImportOutletId(e.target.value)}
                                    disabled={importing || isLoadingBranchSelection}
                                    className="w-full px-4 py-3 text-xs transition border border-neutral-200 bg-white rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                                >
                                    <option value="">Pilih outlet dulu...</option>
                                    {importBranchOptions.map((b) => (
                                        <option key={b.id} value={b.id}>{b.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <label
                            title={!effectiveImportOutletId ? "Pilih outlet tujuan upload dulu" : "Upload file initial data"}
                            className={`inline-flex items-center justify-center px-4 py-3 space-x-2 text-white transition rounded-xl ${(importing || !effectiveImportOutletId) ? "bg-neutral-400 cursor-not-allowed" : "bg-brand-800 cursor-pointer active:hover:scale-95"}`}
                        >
                            <span>{importing ? "Mengimpor..." : "Upload Initial Data"}</span>
                            <input
                                type="file"
                                accept=".xlsx,.xls"
                                className="hidden"
                                disabled={importing || !effectiveImportOutletId}
                                onChange={handleImportFile}
                            />
                        </label>
                        {/* ATALLA-353: centang bila qty di file dalam LUSIN -> backend kalikan 12 ke PCS. */}
                        <label className="inline-flex items-center gap-2 text-xs text-neutral-600 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={convertLusinToPcs}
                                onChange={(e) => setConvertLusinToPcs(e.target.checked)}
                                disabled={importing}
                                className="w-4 h-4 rounded border-neutral-300"
                            />
                            <span>Convert LUSIN → PCS (×12)</span>
                        </label>
                        {!isBranchLocked && !effectiveImportOutletId && (
                            <p className="text-xs text-neutral-400">Pilih outlet dulu untuk mengaktifkan upload.</p>
                        )}
                    </div>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-4 text-xs">
                    {!isBranchLocked && (
                        <div>
                            <Label value="Outlet" />
                            <OutletFilterSelect
                                value={outletId}
                                onChange={(v) => mergeParams({ outlet_id: v })}
                                className="w-full px-4 py-3 text-xs transition border border-neutral-200 bg-white rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                                title="Outlet"
                            />
                        </div>
                    )}
                    <div>
                        <Label value="Status PO" />
                        <select
                            value={status}
                            onChange={(e) => mergeParams({ status: e.target.value })}
                            className="w-full px-4 py-3 text-xs transition border border-neutral-200 bg-white rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                        >
                            {STATUS_OPTIONS.map((opt) => (
                                <option key={opt.id} value={opt.id}>{opt.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label htmlFor="from" value="Dari Tanggal PO" />
                        <InputDate id="from" maxDate={Date.parse(to)} onChange={(value) => mergeParams({ from: moment(value).format("Y-MM-DD") })} value={Date.parse(from)} selected={Date.parse(from)} />
                    </div>
                    <div>
                        <Label htmlFor="to" value="Sampai Tanggal PO" />
                        <InputDate id="to" minDate={Date.parse(from)} onChange={(value) => mergeParams({ to: moment(value).format("Y-MM-DD") })} value={Date.parse(to)} selected={Date.parse(to)} />
                    </div>
                    <div>
                        {/* ATALLA-356: toggle satuan tampilan qty (display saja, nilai dasar PCS). */}
                        <Label value="Satuan Tampilan" />
                        <select
                            value={displayUnit}
                            onChange={(e) => mergeParams({ unit: e.target.value })}
                            className="w-full px-4 py-3 text-xs transition border border-neutral-200 bg-white rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                        >
                            <option value="PCS">PCS</option>
                            <option value="LUSIN">LUSIN</option>
                        </select>
                    </div>
                    <div className="flex items-end">
                        {/* ATALLA-354: export xlsx sesuai filter aktif. */}
                        <button
                            type="button"
                            onClick={handleExport}
                            disabled={rows.length === 0}
                            className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-xs font-semibold text-white rounded-xl transition ${rows.length === 0 ? "bg-neutral-400 cursor-not-allowed" : "bg-green-600 active:hover:scale-95"}`}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" fill="none">
                                <path d="M12 3v12" /><path d="M7 10l5 5l5 -5" /><path d="M5 21h14" />
                            </svg>
                            <span>Export Excel</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 text-xs">
                <div className="p-4 bg-white border rounded-2xl border-neutral-200">
                    <div className="text-neutral-500">Jumlah PO</div>
                    <div className="mt-1 text-lg font-semibold text-neutral-900">{formatNumber(summary.po_count)}</div>
                </div>
                <div className="p-4 bg-white border rounded-2xl border-neutral-200">
                    <div className="text-neutral-500">Qty Dipesan ({displayUnit})</div>
                    <div className="mt-1 text-lg font-semibold text-neutral-900">{formatQtyByUnit(summary.total_ordered, displayUnit)}</div>
                </div>
                <div className="p-4 bg-white border rounded-2xl border-neutral-200">
                    <div className="text-neutral-500">Qty Diterima ({displayUnit})</div>
                    <div className="mt-1 text-lg font-semibold text-neutral-900">{formatQtyByUnit(summary.total_received, displayUnit)}</div>
                </div>
                <div className="p-4 bg-white border rounded-2xl border-neutral-200">
                    <div className="text-neutral-500">Sisa Belum Diterima ({displayUnit})</div>
                    <div className="mt-1 text-lg font-semibold text-orange-600">{formatQtyByUnit(summary.total_outstanding, displayUnit)}</div>
                </div>
            </div>

            <div className="overflow-x-auto bg-white border rounded-2xl border-neutral-200">
                <table className="min-w-full text-xs">
                    <thead>
                        <tr className="bg-neutral-100 text-neutral-700">
                            <th className="px-4 py-3 font-semibold text-left">NO. PO</th>
                            <th className="px-4 py-3 font-semibold text-left">TGL PO</th>
                            <th className="px-4 py-3 font-semibold text-left">ESTIMASI</th>
                            <th className="px-4 py-3 font-semibold text-left">OUTLET</th>
                            <th className="px-4 py-3 font-semibold text-left">SUPPLIER</th>
                            <th className="px-4 py-3 font-semibold text-left">STATUS</th>
                            <th className="px-4 py-3 font-semibold text-left">PRODUK</th>
                            <th className="px-4 py-3 font-semibold text-right">QTY PESAN</th>
                            <th className="px-4 py-3 font-semibold text-right">QTY TERIMA</th>
                            <th className="px-4 py-3 font-semibold text-right">SISA</th>
                        </tr>
                    </thead>
                    <tbody>
                        {!isLoading && rows.length === 0 && (
                            <tr>
                                <td colSpan={10} className="px-4 py-8 text-center text-neutral-500">
                                    Tidak ada PO outstanding pada filter ini.
                                </td>
                            </tr>
                        )}
                        {groupedByPo.map((poRows) =>
                            poRows.map((row, idx) => (
                                <tr key={`${row.po_id}-${row.product_unit_id}-${idx}`} className="border-t border-neutral-200 hover:bg-brand-50 transition-colors">
                                    <td className="px-4 py-2 whitespace-nowrap font-medium text-neutral-900">{idx === 0 ? row.po_number : ""}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-neutral-800">{idx === 0 ? formatDateID(row.po_date) : ""}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-neutral-800">{idx === 0 ? (row.estimate_date ? formatDateID(row.estimate_date) : "-") : ""}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-neutral-800">{idx === 0 ? (branchNameById.get(String(row.outlet_id)) ?? row.outlet_id) : ""}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-neutral-800">{idx === 0 ? (row.supplier_name || "-") : ""}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-neutral-800">{idx === 0 ? row.status : ""}</td>
                                    <td className="px-4 py-2 whitespace-normal break-words leading-5 text-neutral-800">{row.product_name}</td>
                                    <td className="px-4 py-2 text-right text-neutral-800">{formatQtyByUnit(row.qty_ordered, displayUnit)}</td>
                                    <td className="px-4 py-2 text-right text-neutral-800">{formatQtyByUnit(row.qty_received, displayUnit)}</td>
                                    <td className="px-4 py-2 text-right font-semibold text-orange-600">{formatQtyByUnit(row.qty_outstanding, displayUnit)}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                {/* 290726: paginasi PER PO — satu halaman memuat SELURUH item milik
                    PO-PO di halaman itu, jadi `total` di bawah adalah jumlah PO, bukan
                    jumlah baris. Navigasi lewat mergeParams (bukan `links` dari backend)
                    supaya filter di URL — from/to/unit — tidak ikut hilang saat pindah. */}
                {!isLoading && rows.length > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 border-t border-neutral-200">
                        <div className="text-xs text-neutral-600">
                            Menampilkan <span className="font-medium">{reportData?.row_count ?? rows.length}</span> baris item
                            {" "}dari PO ke-<span className="font-medium">{reportData?.from ?? 0}</span>
                            –<span className="font-medium">{reportData?.to ?? 0}</span>
                            {" "}(total <span className="font-medium">{reportData?.total ?? 0}</span> PO).
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                disabled={page <= 1}
                                onClick={() => mergeParams({ page: String(page - 1) })}
                                className="px-3 py-2 text-xs font-medium border rounded-lg border-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-neutral-100"
                            >
                                Sebelumnya
                            </button>
                            <span className="text-xs text-neutral-600">
                                Halaman {reportData?.current_page ?? page} / {reportData?.last_page ?? 1}
                            </span>
                            <button
                                type="button"
                                disabled={(reportData?.current_page ?? page) >= (reportData?.last_page ?? 1)}
                                onClick={() => mergeParams({ page: String(page + 1) })}
                                className="px-3 py-2 text-xs font-medium border rounded-lg border-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-neutral-100"
                            >
                                Berikutnya
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
